'use client';

import React from 'react';
import {
    Drawer, List, ListItem, ListItemButton, ListItemIcon,
    ListItemText, Toolbar, Box, Typography, Divider,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Folder as FolderIcon,
    ReceiptLong as ReceiptIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
} from '@mui/icons-material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import QueuePlayNextIcon from '@mui/icons-material/QueuePlayNext';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { BanknoteArrowUp } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import apiClient from '../../services/api-client';

const menuItems = [
    { text: 'Mon espace', icon: <DashboardIcon />, path: '/agent-dashboard', roles: ['ROLE_AGENT'] },
    { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/dashboard', roles: ['ROLE_ADMIN', 'ROLE_CHEF_AGENCE'] },
    { text: 'Nouveau dossier', icon: <CreateNewFolderIcon />, path: '/orders/create', roles: ['ROLE_AGENT', 'ROLE_ADMIN', 'ROLE_CHEF_AGENCE'] },
    { text: 'Clients', icon: <PeopleIcon />, path: '/clients', roles: ['ROLE_AGENT', 'ROLE_ADMIN', 'ROLE_CHEF_AGENCE'] },
    { text: 'Dossiers file', icon: <QueuePlayNextIcon />, path: '/orders/validation', roles: ['ROLE_ADMIN', 'ROLE_CHEF_AGENCE'] },
    { text: 'Dossiers', icon: <FolderIcon />, path: '/orders', roles: ['ROLE_AGENT', 'ROLE_ADMIN', 'ROLE_CHEF_AGENCE'] },
    { text: 'Facturation', icon: <ReceiptIcon />, path: '/factures/recap', roles: ['ROLE_AGENT', 'ROLE_ADMIN', 'ROLE_CHEF_AGENCE'] },
    { text: 'Paiement', icon: <BanknoteArrowUp />, path: '/factures/paiements', roles: ['ROLE_AGENT', 'ROLE_ADMIN', 'ROLE_CHEF_AGENCE'] },
    { text: 'Personnel', icon: <ManageAccountsIcon />, path: '/personnel', roles: ['ROLE_ADMIN'] },
    { text: 'Reporting', icon: <AssessmentIcon />, path: '/reporting', roles: ['ROLE_ADMIN'] },
    { text: 'Paramètres', icon: <SettingsIcon />, path: '/settings', roles: ['ROLE_AGENT', 'ROLE_ADMIN', 'ROLE_CHEF_AGENCE'] },
];

export function Sidebar({ open, onClose, isMobile, drawerWidth }) {
    const location = useLocation();
    const navigate = useNavigate();

    // Récupération du store et des rôles
    const { refreshToken, logout, user } = useAuthStore();
    const userRoles = user?.roles || [];

    const handleLogout = async () => {
        try {
            await apiClient.post('/api/auth/logout', { refreshToken });
        } catch (error) {
            console.error("Erreur déconnexion:", error);
        } finally {
            logout();
            navigate('/login', { replace: true });
        }
    };

    // Filtrage dynamique des menus
    const visibleMenuItems = menuItems.filter(item =>
        item.roles.some(role => userRoles.includes(role))
    );

    const drawerContent = (
        <>
            <Toolbar sx={{ px: 3, py: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="h1" sx={{ color: 'primary.main', fontSize: '1.8rem', fontFamily: "'Cormorant Garamond', serif" }}>
                    Miracle Travel
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', letterSpacing: 3, mt: 0.5, fontWeight: 600, fontSize: '0.65rem' }}>
                    TOURS & SERVICES
                </Typography>
            </Toolbar>

            <Box sx={{ px: 2, flexGrow: 1 }}>
                <List disablePadding>
                    {visibleMenuItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    onClick={() => {
                                        navigate(item.path);
                                        if (isMobile) onClose();
                                    }}
                                    sx={{
                                        borderRadius: '12px',
                                        py: 1.2,
                                        bgcolor: isActive ? 'rgba(201,168,76,0.15)' : 'transparent',
                                        transition: 'all 0.3s ease',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', transform: 'translateX(4px)' },
                                    }}
                                >
                                    <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'rgba(255,255,255,0.5)', minWidth: 42 }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.text}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontWeight: isActive ? 600 : 400,
                                            color: isActive ? 'primary.main' : 'rgba(255,255,255,0.8)',
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </Box>

            <Box sx={{ p: 2, mt: 'auto' }}>
                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                <ListItemButton
                    onClick={handleLogout}
                    sx={{
                        borderRadius: '12px',
                        color: 'rgba(255,255,255,0.5)',
                        '&:hover': { color: '#ff4d4d', bgcolor: 'rgba(255, 77, 77, 0.08)' },
                    }}
                >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 42 }}>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Déconnexion" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                </ListItemButton>
            </Box>
        </>
    );

    const commonPaperSx = {
        width: drawerWidth,
        bgcolor: 'secondary.main',
        color: 'white',
        borderRight: 'none',
        display: 'flex',
        flexDirection: 'column',
    };

    return (
        <>
            {isMobile ? (
                <Drawer variant="temporary" open={open} onClose={onClose} sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': commonPaperSx }}>
                    {drawerContent}
                </Drawer>
            ) : (
                <Drawer variant="permanent" open sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': commonPaperSx }}>
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
}